import { mergeAttributes } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { nanoid } from "nanoid";

import { router } from "@/main";

import ListItem from "../ListItem";
import styles from "./CoordinationListItem.module.scss";
import { icon } from "./icons";
import {
  findChildOfType,
  findListItemFromEventTarget,
  getCollapsed,
  storeCollapsed,
} from "./utils";

const getUrlParameter = (name: string) => {
  name = name.replace(/[\[]/, "\\[").replace(/[\]]/, "\\]");
  const regex = new RegExp(`[\\?&]${name}=([^&#]*)`);
  const results = regex.exec(window.location.search);
  return results === null
    ? null
    : decodeURIComponent(results[1].replace(/\+/g, " ")).replace(/\/$/, "");
};

export const CoordinationListItem = ListItem.extend({
  priority: 1000,
  group: "block",

  content: "block+",

  addStorage() {
    return { hover: {} };
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      syncedBlockId: {
        keepOnSplit: false,
      },
      syncedBlockProject: {
        keepOnSplit: false,
      },
      changeId: {
        keepOnSplit: false,
      },
      hasNodePage: {
        rendered: true,
        default: false,
        keepOnSplit: false,
      },
      blockId: {
        rendered: true,
        default: null,
        parseHTML: (element) => element.getAttribute(`data-blockId`),
        renderHTML: (attributes) => {
          return attributes.blockId
            ? { [`data-blockId`]: attributes.blockId, [`blockId`]: attributes.blockId }
            : {};
        },
        keepOnSplit: false,
      },
      votes: {
        rendered: false,
        default: [],
        keepOnSplit: false,
      },
      hover: {
        default: false,
        rendered: false,
        keepOnSplit: false,
      },
    };
  },

  parseHTML() {
    return [{ tag: 'li[data-type="draggable-item"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "li",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        "data-type": "draggable-item",
      }),
      0,
    ];
  },

  addProseMirrorPlugins() {
    const editor = this.editor;
    const type = this.type;
    return [
      new Plugin({
        key: new PluginKey("eventHandler"),
        appendTransaction: (_transactions, oldState, newState) => {
          // no changes
          if (newState.doc === oldState.doc) {
            return;
          }
          const tr = newState.tr;

          newState.doc.descendants((node, pos, parent) => {
            if (
              node.isBlock &&
              // parent === newState.doc &&
              !node.attrs.blockId &&
              node.type.name == type.name
            ) {
              console.log("Yup");
              tr.setNodeMarkup(pos, undefined, {
                ...node.attrs,
                blockId: nanoid(),
              }).setMeta("addToHistory", false);
            }
          });

          return tr;
        },
        props: {
          // handleKeyDown(view, event) {
          //   if (event.key === "Enter") {
          //     const { state, dispatch } = view;
          //     const { selection } = state;
          //     if (selection.empty && selection.$from.parent.type === type) {
          //       // Check if the Enter key is pressed within a list item
          //       const transaction = state.tr;
          //       if (!selection.$from.parent.attrs.blockId) {
          //         // Assign a new block ID only if it doesn't exist
          //         const newBlockId = nanoid();
          //         transaction.setNodeMarkup(selection.$from.before(), null, {
          //           ...selection.$from.parent.attrs,
          //           blockId: newBlockId,
          //         });
          //       }
          //       dispatch(transaction);
          //       return true;
          //     }
          //   }
          //   return false;
          // },

          handleDOMEvents: {
            cut: (view, event) => {
              console.log("CUT");
              // lastOperationWasCut = true;
              // // You might want to reset this flag after a short delay
              // setTimeout(() => lastOperationWasCut = false, 1000);
              return false;
            },
            mouseover(view, event) {
              const target = event.target as Element;
              const [node, pos] = findListItemFromEventTarget(target, view);
              if (!pos || !node) return;
              const transaction = view.state.tr
                .setNodeAttribute(pos, "changeId", nanoid())
                .setMeta("addToHistory", false);
              editor.storage.hover = node.attrs.blockId;
              view.dispatch(transaction);
              return false;
            },
            mouseout(view, event) {
              const target = event.target as Element;
              const [node, pos] = findListItemFromEventTarget(target, view);
              if (!pos || !node) return;
              const transaction = view.state.tr
                .setNodeAttribute(pos, "changeId", nanoid())
                .setMeta("addToHistory", false);
              editor.storage.hover = null;
              view.dispatch(transaction);
              return false;
            },
          },
        },
      }),
    ];
  },

  addNodeView() {
    const debug = getUrlParameter("debug");
    return ({ editor, HTMLAttributes, node, getPos }) => {
      const { view } = editor;
      const collapsed = getCollapsed(node.attrs.blockId);

      const dom = document.createElement("li");
      dom.className = `${styles.listItem} ${collapsed ? styles.collapsed : ""} draggable-item`;
      dom.addEventListener("click", (e) => {
        console.log("Click!");
      });

      // Collapse
      const collapse = document.createElement("div");
      collapse.className = styles.collapse;
      collapse.contentEditable = "false";
      collapse.append(icon("angle-right"));
      collapse.append(icon("angle-down"));
      collapse.addEventListener("click", async (e) => {
        e.stopPropagation();
        console.log(e, Boolean(findChildOfType(node, "bulletList")));
        const collapsed = getCollapsed(node.attrs.blockId);
        storeCollapsed(node.attrs.blockId, !collapsed);
        console.log(node, getPos);
        if (typeof getPos === "function") {
          view.dispatch(
            view.state.tr
              .setNodeAttribute(getPos(), "changeId", nanoid())
              .setMeta("addToHistory", false)
          );
        }
      });
      dom.append(collapse);

      // Bullet point
      const bullet = document.createElement("div");
      bullet.classList.add(styles.bullet);
      // bullet.title = node.attrs.blockId;
      // bullet.dataset.dragHandle = "true";
      bullet.addEventListener("click", (e) => {
        e.stopPropagation();
        const projectId = router?.state?.matches?.[0].params?.projectId;
        window.coordinationNavigate(`/project/${projectId}/${node.attrs.blockId}`, {});
      });
      if (node?.attrs?.hasNodePage === true) bullet.classList.add(styles.hasNodePage);
      bullet.contentEditable = "false";

      const dot = document.createElement("div");
      dot.className = styles.dot;
      dot.contentEditable = "false";

      bullet.append(dot);

      // Node content
      const nodeContent = document.createElement("div");
      nodeContent.className = styles.nodeContent;

      // Upvotes
      const votes = document.createElement("div");
      votes.className = `${styles.upvotes} ${styles.green}`;
      votes.contentEditable = "false";
      votes.style.display = "none";
      if (node.attrs.votes?.length) {
        const voteCount = node.attrs.votes
          .map((item) => item.value)
          .reduce((prev, next) => prev + next);
        if (voteCount !== 0) {
          votes.innerHTML = voteCount > 0 ? `+${voteCount}` : `-${voteCount}`;
          votes.classList.add(voteCount > 0 ? styles.green : styles.red);
          votes.style.display = "inline";
        }
      }

      // Editable content
      const content = document.createElement("div");

      const id = document.createElement("div");
      id.className = styles.id;
      id.contentEditable = "false";
      id.innerHTML = node.attrs.blockId;
      nodeContent.append(id, content);
      if (debug) {
        id.style.display = "block";
      }

      nodeContent.append(votes, content);

      // Append all elements to the node view container
      dom.append(bullet, nodeContent);

      Object.entries(this.options.HTMLAttributes).forEach(([key, value]) => {
        dom.setAttribute(key, value);
      });

      Object.entries(HTMLAttributes).forEach(([key, value]) => {
        dom.setAttribute(key, value);
      });

      return {
        dom,
        contentDOM: content,
        update: (updatedNode) => {
          if (updatedNode.type !== this.type || updatedNode.attrs.blockId !== node.attrs.blockId) {
            return false;
          }

          bullet.classList.toggle(styles.hasNodePage, updatedNode?.attrs?.hasNodePage);

          if (updatedNode.attrs.votes?.length) {
            const voteCount = updatedNode.attrs.votes
              .map((item) => item.value)
              .reduce((prev, next) => prev + next);
            if (voteCount !== 0) {
              votes.innerHTML = voteCount > 0 ? `+${voteCount}` : `-${voteCount}`;
              votes.classList.add(voteCount > 0 ? styles.green : styles.red);
              votes.style.display = "inline";
            } else if (voteCount === 0) {
              votes.style.display = "none";
            }
          }

          id.innerHTML = node.attrs.blockId;
          if (debug) {
            id.style.display = "block";
          } else {
            id.style.display = "none";
          }

          // If has child node
          if (Boolean(findChildOfType(updatedNode, "bulletList"))) {
            collapse.classList.toggle(
              styles.visible,
              editor.storage.hover == updatedNode.attrs.blockId
            );
            let collapsed = getCollapsed(node.attrs.blockId);
            dom.classList.toggle(styles.collapsed, collapsed);
          } else {
            dom.classList.remove(styles.collapsed);
          }

          return true;
        },
      };
    };
  },
});
